/** Classify and summarise booking-confirmation / course-reminder rows for the session roster. */

export type RosterEmailKind = 'booking_conf' | 'reminder' | 'certificate';

export type RosterEmailKindStatus = {
  status: 'sent' | 'failed' | 'not_sent';
  at?: string | null;
  by?: string | null;
};

export type RosterEmailIndicators = {
  bookingConf: RosterEmailKindStatus;
  reminder: RosterEmailKindStatus;
  certificate: RosterEmailKindStatus;
};

export const EMPTY_ROSTER_EMAIL_INDICATORS: RosterEmailIndicators = {
  bookingConf: { status: 'not_sent' },
  reminder: { status: 'not_sent' },
  certificate: { status: 'not_sent' },
};

const SENT_STATUSES = new Set(['sent', 'delivered', 'pending', 'processing', 'queued', 'approved']);
const FAILED_STATUSES = new Set(['failed', 'bounced', 'permanently_failed']);

export function classifyRosterEmailKind(args: {
  emailType?: string | null;
  subject?: string | null;
  templateName?: string | null;
  metadata?: Record<string, unknown> | null;
}): RosterEmailKind | null {
  const emailType = String(args.emailType || '').trim().toLowerCase();
  const metaName = String(
    args.templateName ||
      args.metadata?.templateName ||
      args.metadata?.template_name ||
      args.metadata?.emailTemplateName ||
      ''
  ).trim();
  const subject = String(args.subject || args.metadata?.subject || '').trim();
  const haystack = `${metaName} ${subject}`.toLowerCase();

  const metaKind = String(args.metadata?.kind || '').trim().toLowerCase();
  if (metaKind === 'certificate' || emailType === 'certificate') return 'certificate';
  if (emailType === 'booking_confirmation') return 'booking_conf';
  if (emailType === 'weekly_reminder' || emailType === '24h_reminder') return 'reminder';
  if (emailType === 'certificate_reminder') return null;

  if (/certificate\s*reminder/i.test(haystack)) return null;
  if (/\bcertificate\b/i.test(haystack) && /sent|completion|attached/i.test(haystack)) return 'certificate';
  if (/booking\s*conf/i.test(haystack) || (/confirmation/i.test(haystack) && /booking/i.test(haystack))) {
    return 'booking_conf';
  }
  if (/course\s*reminder/i.test(haystack) || (/\breminder\b/i.test(haystack) && !/certificate/i.test(haystack))) {
    return 'reminder';
  }
  return null;
}

export function mapOutboxStatus(raw: string | null | undefined): 'sent' | 'failed' | 'not_sent' {
  const s = String(raw || '').trim().toLowerCase();
  if (FAILED_STATUSES.has(s)) return 'failed';
  if (SENT_STATUSES.has(s)) return 'sent';
  if (!s) return 'not_sent';
  // Unknown non-empty status: treat as in-flight / sent for roster visibility
  return 'sent';
}

export function recipientEmailKey(email: string | null | undefined): string {
  return String(email || '').trim().toLowerCase();
}

export function pickInitiatorName(metadata: Record<string, unknown> | null | undefined): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const name =
    metadata.initiated_by_name ||
    metadata.initiatedByName ||
    metadata.sent_by_name ||
    metadata.sentByName ||
    metadata.actorName ||
    metadata.actor_name;
  const s = String(name || '').trim();
  return s || null;
}

export function buildRosterEmailTitle(
  label: string,
  info: RosterEmailKindStatus,
  formatDateTime: (iso: string | null | undefined) => string
): string {
  const statusLabel =
    info.status === 'sent' ? 'Sent' : info.status === 'failed' ? 'Failed' : 'Not sent';
  const lines = [`${label}`, `Status: ${statusLabel}`];
  if (info.at) lines.push(`Date: ${formatDateTime(info.at)}`);
  if (info.by) lines.push(`Sent by: ${info.by}`);
  return lines.join('\n');
}

/** Prefer a failed row over sent when both exist? No — prefer latest by time; failed wins only if latest. */
export function mergeKindStatus(
  prev: RosterEmailKindStatus | undefined,
  next: RosterEmailKindStatus
): RosterEmailKindStatus {
  if (!prev || prev.status === 'not_sent') return next;
  if (next.status === 'not_sent') return prev;
  const prevAt = prev.at ? Date.parse(prev.at) : 0;
  const nextAt = next.at ? Date.parse(next.at) : 0;
  if (nextAt >= prevAt) return next;
  return prev;
}